import { TODO_CTA_MESSAGE } from '@littlebird/action-cards';

import { isDoneStatus } from '../registry/internals/cardStatus';
import { resolveUrgency } from '../registry/internals/urgency';

import { withdrawnTextOf } from './historyRowView';

import type { CardDoneVia, CardState } from '@littlebird/action-cards-view';

export type FeedPillIcon = NonNullable<NonNullable<CardState['run']>['icon']>;

export type FeedPrimaryPress =
  | { kind: 'run'; key: string }
  // `via` says how the user marked it done, for analytics
  | { kind: 'acknowledge'; via?: CardDoneVia }
  | { kind: 'chat'; prompt: string }
  // Opens the chat already started from the card
  | { kind: 'continue' };

export type FeedStakeTone = 'muted' | 'warn' | 'danger';

export type FeedOutcomeTone = 'done' | 'danger' | 'muted';

export type FeedRowView = {
  // The object the card is about; the card's own title on a row served before the fact
  title: string;
  // The rest verb; absent on a Littlebird update and on a row served before the fact
  verb: string | null;
  age: string | null;
  stake: { label: string; tone: FeedStakeTone } | null;
  // The primary as the row's pill; absent unless the row serves its run words and action
  pill: {
    press: FeedPrimaryPress;
    label: string;
    runningLabel: string;
    icon: FeedPillIcon | null;
  } | null;
  running: boolean;
  failed: boolean;
  // Replaces "‹verb› · ‹age›" once the card has run or failed. A to-do or notice marked done shows only a tick, which
  // the text names for screen readers
  outcome: { text: string; tone: FeedOutcomeTone; tick?: boolean } | null;
  openUrl: string | null;
};

// The Feed acts on a draft or failed card: it offers the card's button, snooze and dismiss
export const isActionableStatus = (status: CardState['status']): boolean =>
  status === 'draft' || status === 'failed';

// The backend marks only a draft done; a failed to-do keeps its own button and its retry
export const offersMarkDone = (
  todo: boolean,
  status: CardState['status']
): boolean => todo && status === 'draft';

const STAKE_TONE = { default: 'muted', soon: 'warn', late: 'danger' } as const;

const ACKNOWLEDGE = 'acknowledge';

// For a row served before `/run/failed_text`
export const FAILED_ROW_TEXT = "Couldn't send";

const stakeOf = (
  urgency: CardState['urgency'],
  now: Date
): FeedRowView['stake'] => {
  if (!urgency) return null;
  const resolved =
    urgency.mode === 'event'
      ? { label: urgency.label, tone: urgency.tone }
      : resolveUrgency(
          {
            label: urgency.label,
            actBy: urgency.act_by ?? undefined,
            tone: urgency.tone,
          },
          now
        );
  if (!/[\p{L}\p{N}]/u.test(resolved.label)) return null;
  return { label: resolved.label, tone: STAKE_TONE[resolved.tone] };
};

// A Littlebird to-do's due date is the expiry its agent chose, often with nothing in the source behind it, so the
// Feed leaves it off until to-dos carry a real deadline. A run's "expires …" on a to-do still shows, and a task app's
// own due date belongs to its own card type, never a to-do
const isTodoDue = (state: CardState): boolean =>
  state.fields.kind === 'todo' &&
  !!state.urgency &&
  !state.urgency.label.startsWith('expires');

const outcomeOf = (state: CardState): FeedRowView['outcome'] => {
  if (state.status === 'failed')
    return { text: state.run?.failed_text ?? FAILED_ROW_TEXT, tone: 'danger' };
  // Without this the row keeps the label the card had as a draft, such as "Reply drafted"
  if (state.status === 'revoked')
    return { text: withdrawnTextOf(state), tone: 'muted' };
  if (!isDoneStatus(state.status)) return null;
  const text = state.run?.done_text ?? state.outcome?.label;
  if (!text) return null;
  const tick =
    state.run?.action === ACKNOWLEDGE || state.fields.kind === 'todo';
  return tick ? { text, tone: 'done', tick } : { text, tone: 'done' };
};

// Line 1 of the opened frame. A frame-only card, whose primary is Got it, has no object
// for its title to land in, so its header keeps the title
export const frameHappensOf = (state: CardState): string | null =>
  state.run?.action === ACKNOWLEDGE ? null : (state.happens ?? null);

const CONTINUE_WORKING = 'Continue working';

// A to-do leads with its own button until a chat is started from it, then with one that continues that chat. A chat
// Littlebird started keeps the to-do's own label until the user writes in it, and the button reopens that chat
const todoCtaOf = (state: CardState): FeedRowView['pill'] => {
  const { kind, cta_label: label, cta_prompt: prompt } = state.fields;
  if (
    kind === 'todo' &&
    state.chat_auto_started === true &&
    typeof label === 'string'
  )
    return {
      press: { kind: 'continue' },
      label,
      runningLabel: label,
      icon: null,
    };
  if (kind === 'todo' && state.chat_started === true)
    return {
      press: { kind: 'continue' },
      label: CONTINUE_WORKING,
      runningLabel: CONTINUE_WORKING,
      icon: null,
    };
  if (
    kind !== 'todo' ||
    typeof label !== 'string' ||
    typeof prompt !== 'string'
  )
    return null;
  return {
    press: { kind: 'chat', prompt: TODO_CTA_MESSAGE },
    label,
    runningLabel: label,
    icon: null,
  };
};

// The feed row reads only served facts; any one of them may be missing on an older row.
// `lastActiveAt` replaces the served age when the host knows of later activity on the card
export function feedRowView(
  state: CardState,
  {
    runPending = false,
    now = new Date(),
    lastActiveAt,
  }: { runPending?: boolean; now?: Date; lastActiveAt?: string } = {}
): FeedRowView {
  const { run } = state;
  const running = runPending || state.status === 'running';
  const runnable = state.status === 'draft' || running;
  return {
    title: state.row_title ?? state.title,
    verb: state.verb ?? null,
    age: lastActiveAt ?? state.created_at ?? null,
    stake:
      isDoneStatus(state.status) || isTodoDue(state)
        ? null
        : stakeOf(state.urgency, now),
    pill:
      run?.action && runnable
        ? {
            press:
              run.action === ACKNOWLEDGE
                ? { kind: 'acknowledge' }
                : { kind: 'run', key: run.action },
            label: run.label,
            runningLabel: run.running_label,
            icon: run.icon ?? null,
          }
        : state.status === 'draft'
          ? todoCtaOf(state)
          : null,
    running,
    failed: state.status === 'failed',
    outcome: outcomeOf(state),
    openUrl: state.open_url ?? null,
  };
}
